/**
 * useDensityStream — live crowd density WebSocket hook.
 *
 * Connects to the Crowd API WebSocket at VITE_CROWD_WS_URL/events (Socket.IO namespace).
 * After PRIMARY_FAIL_THRESHOLD consecutive connect failures it transparently falls
 * back to the simulation WebSocket at VITE_SIM_WS_URL (no namespace).
 *
 * Both endpoints publish density_update events as WSMessage<DensitySnapshot>.
 * The hook normalises them and returns a keyed map so components only re-render
 * the zone that changed.
 *
 * No PII. All DensitySnapshot values are anonymous aggregates.
 */

import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import type { DensitySnapshot } from '@vigilo/shared';
import { CROWD_SIMULATION_RESET_EVENT } from './useSimulation';

// Controlled by .env — defaults let the app start with no config.
const PRIMARY_WS_URL = (import.meta.env['VITE_CROWD_WS_URL'] as string | undefined)
  ?? (import.meta.env['VITE_API_GATEWAY_WS_URL'] as string | undefined)
  ?? (import.meta.env['VITE_CORE_WS_URL'] as string | undefined)
  ?? 'http://localhost:3011';
const SIM_WS_URL = (import.meta.env['VITE_SIM_WS_URL'] as string | undefined)
  ?? 'http://localhost:3002';
const PRIMARY_SOCKET_PATH = (import.meta.env['VITE_CROWD_SOCKET_PATH'] as string | undefined)
  ?? (import.meta.env['VITE_API_GATEWAY_SOCKET_PATH'] as string | undefined)
  ?? (import.meta.env['VITE_CORE_SOCKET_PATH'] as string | undefined);
const SIM_SOCKET_PATH = import.meta.env['VITE_SIM_SOCKET_PATH'] as string | undefined;

// Give up on Crowd API and switch to simulation after this many connect_errors.
const PRIMARY_FAIL_THRESHOLD = 3;

export type StreamSource = 'crowd-api' | 'core' | 'simulation' | 'connecting' | 'mock';

export interface DensityStreamState {
  snapshots:      Record<string, DensitySnapshot>;
  connected:      boolean;
  source:         StreamSource;
  lastEventAt:    Date | null;
  eventsReceived: number;
}

export function useDensityStream(): DensityStreamState {
  const [snapshots,      setSnapshots]      = useState<Record<string, DensitySnapshot>>({});
  const [connected,      setConnected]      = useState(false);
  const [source,         setSource]         = useState<StreamSource>('connecting');
  const [lastEventAt,    setLastEventAt]    = useState<Date | null>(null);
  const [eventsReceived, setEventsReceived] = useState(0);

  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    let active        = true;
    let failCount     = 0;
    let usingFallback = false;

    function onDensityUpdate(msg: { payload?: DensitySnapshot }) {
      if (!active) return;
      const snap = msg?.payload;
      if (snap?.zoneId) {
        setSnapshots(prev => ({ ...prev, [snap.zoneId]: snap }));
        setLastEventAt(new Date());
        setEventsReceived(prev => prev + 1);
      }
    }

    function onSimulationReset() {
      setSnapshots({});
      setLastEventAt(null);
      setEventsReceived(0);
    }
    window.addEventListener(CROWD_SIMULATION_RESET_EVENT, onSimulationReset);

    function connectSimulation() {
      if (!active || usingFallback) return;
      usingFallback = true;

      socketRef.current?.disconnect();
      const simSock = io(SIM_WS_URL, { reconnection: true, timeout: 8000, path: SIM_SOCKET_PATH });
      socketRef.current = simSock;

      simSock.on('connect',       () => { if (active) { setConnected(true); setSource('simulation'); } });
      simSock.on('disconnect',    () => { if (active) setConnected(false); });
      simSock.on('connect_error', () => { if (active) setConnected(false); });
      simSock.on('density_update', onDensityUpdate);
    }

    // Try Crowd API first; namespace /events is exposed by the crowd live-stream facade.
    const coreSock = io(`${PRIMARY_WS_URL}/events`, {
      reconnection:        true,
      reconnectionAttempts: PRIMARY_FAIL_THRESHOLD,
      timeout:             5000,
      path:                PRIMARY_SOCKET_PATH,
    });
    socketRef.current = coreSock;

    coreSock.on('connect', () => {
      if (!active) return;
      failCount = 0;
      setConnected(true);
      setSource(PRIMARY_WS_URL.includes('3001') ? 'core' : 'crowd-api');
    });

    coreSock.on('disconnect', () => {
      if (active && !usingFallback) setConnected(false);
    });

    coreSock.on('connect_error', () => {
      if (!active || usingFallback) return;
      failCount++;
      setConnected(false);
      if (failCount >= PRIMARY_FAIL_THRESHOLD) connectSimulation();
    });

    // socket.io fires this after all reconnectionAttempts are exhausted.
    coreSock.on('reconnect_failed', () => {
      if (!usingFallback) connectSimulation();
    });

    coreSock.on('density_update', onDensityUpdate);

    return () => {
      active = false;
      window.removeEventListener(CROWD_SIMULATION_RESET_EVENT, onSimulationReset);
      socketRef.current?.disconnect();
    };
  }, []);

  return { snapshots, connected, source, lastEventAt, eventsReceived };
}
